import type { ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';
import { Flex } from '@chakra-ui/react';
import head from 'lodash-es/head';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCookingClassesByStore } from '../../../../bff-api/stores/useCookingClassesByStore';
import { useCustomerGeoLocationStore } from '../../../../bff-api/stores/useCustomerGeoLocationStore';
import { useCulinaryClassPreferredStore } from '../../../../hooks/useCulinaryClassPreferredStore';
import { Header } from '../../../../ui-components';
import { CarouselCoverflow } from '../../../../ui-components/carousel/coverflow/CarouselCoverflow';
import { type SlideItemProps } from '../../../../ui-components/carousel/coverflow/SlideItem';
import { useCustomPreferences } from '../../../../utils/api';
import { CustomPreference } from '../../../../utils/api/CustomPreference';
import { safeJsonParse } from '../../../../utils/safe-json-parse';
import { GDPDiscoverCookingClassSkeleton } from './GDPSkeleton';
import type { AdminProduct } from '../../../../bff-api/stores/model/AdminProduct';

export const DiscoverCookingClass = (): ReactElement | null => {
    const intl = useIntl();
    const {
        data: storeData,
        isLoading: isLoadingStoreData,
        isFetched: isStoreDataFetched,
    } = useCustomerGeoLocationStore();
    const culinaryClassPreferredStore = useCulinaryClassPreferredStore();

    const customPreferences = useCustomPreferences([
        CustomPreference.GIFT_CARDS_DISCOVER_COOKING_CLASSES,
    ]);

    const cookingClassConfig = safeJsonParse(
        String(customPreferences[CustomPreference.GIFT_CARDS_DISCOVER_COOKING_CLASSES]),
        {
            inStore: '/cooking-classes/in-store-cooking-classes',
            online: '/cooking-classes/online-cooking-classes',
            fallbackStoreId: '600',
        },
    );

    // Determine the primary store ID (nearby or preferred, can be undefined)
    const primaryStoreId = useMemo(() => {
        const preferredStore = culinaryClassPreferredStore.data;
        if (preferredStore) {
            return preferredStore;
        }

        // Wait for geo-location data to finish fetching
        if (isLoadingStoreData || !isStoreDataFetched) {
            return undefined;
        }

        // Return nearby store ID (or undefined if no nearby store)
        return storeData?.id;
    }, [culinaryClassPreferredStore.data, isLoadingStoreData, isStoreDataFetched, storeData?.id]);

    // First fetch with primary store
    const { data: primaryData, isFetching: isPrimaryLoading } =
        useCookingClassesByStore(primaryStoreId);

    // Only fetch fallback if:
    // 1. Geo-location and preferred store finished loading
    // 2. No primary store ID OR primary fetch returned empty results
    // 3. Primary store is different from fallback (if primary exists)
    const shouldFetchFallback =
        isStoreDataFetched &&
        culinaryClassPreferredStore.isFetched &&
        !isPrimaryLoading &&
        primaryStoreId !== cookingClassConfig.fallbackStoreId &&
        (!primaryStoreId || !primaryData?.data || primaryData.data.length === 0);

    // Second fetch (only runs when shouldFetchFallback is true)
    const { data: fallbackData, isFetching: isFallbackLoading } = useCookingClassesByStore(
        shouldFetchFallback ? cookingClassConfig.fallbackStoreId : undefined,
    );

    // Use primary data if available, otherwise fallback
    const cookingClasses = primaryData?.data?.length ? primaryData.data : fallbackData?.data;
    const isLoading = !isStoreDataFetched || isPrimaryLoading || isFallbackLoading;

    const slides: SlideItemProps[] = useMemo(
        () =>
            cookingClasses?.map((cookingClass) => ({
                image: getImageUrl(cookingClass),
                testId: `cooking-class-${cookingClass.id}`,
                title: cookingClass?.name?.default || '',
                chef: intl.formatMessage(
                    {
                        id: 'gift-card-details-page.discover-cooking-class.chef',
                        defaultMessage: 'Chef: {chef}',
                    },
                    { chef: cookingClass.c_instructor || '' },
                ),
                price: cookingClass.price ? `$${cookingClass.price.toFixed(2)}` : '',
                buttonText: intl.formatMessage({
                    id: 'gift-card-details-page.discover-cooking-class.button-text',
                    defaultMessage: 'Book Now',
                }),
                buttonTo: cookingClass.c_pwaUrl,
            })) ?? [],
        [cookingClasses, intl],
    );

    const linkUrl = useMemo(() => {
        if (primaryStoreId && cookingClasses?.length) {
            return `${cookingClassConfig.inStore}?nearestStore=${primaryStoreId}`;
        }
        if (cookingClasses?.length) {
            return cookingClassConfig.inStore;
        }
        return cookingClassConfig.online;
    }, [primaryStoreId, cookingClasses, cookingClassConfig]);

    if (
        !isLoading &&
        (!cookingClasses || (Array.isArray(cookingClasses) && !cookingClasses.length))
    ) {
        return null;
    }

    return (
        <GDPDiscoverCookingClassSkeleton isLoaded={!isLoading}>
            {!isLoading && (
                <Flex gap={8} direction="column">
                    <Flex gap={2} direction="column" px={4}>
                        <Header as="h3" variant="header3" style="bold" textAlign="center">
                            <FormattedMessage
                                id="gift-card-details-page.discover-cooking-class.title"
                                defaultMessage="Discover <green>Cooking Classes</green> <featured>Near You</featured>"
                                values={{
                                    green: GreenText,
                                    featured: (chunks) => FeaturedText(chunks, linkUrl),
                                }}
                            />
                        </Header>
                        <Header
                            as="h4"
                            variant="header4"
                            color="slt-pepper.1000"
                            textAlign="center"
                        >
                            <FormattedMessage
                                id="gift-card-details-page.discover-cooking-class.description"
                                defaultMessage="An Unforgettable, Hands-On Experience"
                            />
                        </Header>
                    </Flex>
                    <CarouselCoverflow slides={slides} />
                </Flex>
            )}
        </GDPDiscoverCookingClassSkeleton>
    );
};

const GreenText = (chunks: ReactNode[]): ReactElement => (
    <span className="text-slt-primary-green-1000">{chunks}</span>
);

const FeaturedText = (chunks: ReactNode[], to: string): ReactElement => (
    <a
        href={to}
        className="relative capitalize text-slt-primary-green-1000 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-[#C6C6C6]"
    >
        {chunks}
    </a>
);

const getImageUrl = (adminProduct: AdminProduct): string | undefined => {
    if (adminProduct.imageGroups) {
        const largeImages = adminProduct.imageGroups.find((ig) => ig.viewType === 'large')?.images;
        return head(largeImages)?.absUrl;
    }
    return undefined;
};
